require("./service-items-table.scss");

import {ChangeDetectionStrategy, Component, OnInit, computed, inject, input, output, signal} from "@angular/core";
import {BreakpointObserver} from "@angular/cdk/layout";
import {toSignal} from "@angular/core/rxjs-interop";
import {map} from "rxjs";
import * as _ from "lodash";
import {bzIconEditOutline, bzIconInfoOutline} from "@bringoz/ui/icons";
import {
  PackageDetailsSettings,
} from "../package-details-settings-panel-content/package-details-settings-panel-content.component";
import {Attachment} from "../../../../attachments/models/attachment.model";
import {SplitService} from "../../../services/split-service.service";
import {PackageDetailsViewSettingsService} from "../../../services/package-details-view-settings.service";
import {MEDIA_GT_MD} from "../../constants/media-breakpoints.constant";
import {SERVICE_ITEMS_TABLE_HEADER_HEIGHT, SERVICE_ITEMS_TABLE_ROW_HEIGHT} from "../../constants/service-items-table.constant";

export interface MissingItemData {
  id?: string;
  reasonId?: string;
  quantity?: number;
  description?: string;
  comment?: string;
  updatedBy?: {source?: string; name?: string};
  actions?: {attachments?: Attachment[]}[];
}

export interface ServiceItemRowData {
  id?: string;
  type?: string;
  size?: string;
  count?: number;
  dimensions?: {length?: number; width?: number; height?: number};
  description?: string;
  barcode?: string;
  sku?: string;
  value?: number;
  labels?: string[];
  pallets?: number;
  packsInItem?: number;
  attachments?: Attachment[];
  missingItems?: MissingItemData[];
}

export type ItemsTableRow =
  | {kind: "item"; item: ServiceItemRowData; packageId: string; packageKnown: boolean}
  | {kind: "missing"; item: MissingItemData; attachments: Attachment[] | undefined};

@Component({
  selector: "app-service-items-table",
  templateUrl: "./service-items-table.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {"[style.height.px]": "height()"},
})
export class ServiceItemsTableComponent implements OnInit {
  readonly items = input<ServiceItemRowData[]>([]);
  readonly settings = input.required<PackageDetailsSettings>();

  readonly edit = output<void>();

  readonly icons = {edit: bzIconEditOutline, info: bzIconInfoOutline};

  readonly currentSettings = signal<PackageDetailsSettings>({});

  private readonly splitService = inject<SplitService>("splitService" as never);
  private readonly viewSettings = inject<PackageDetailsViewSettingsService>("packageDetailsViewSettingsService" as never);
  private viewSettingsChanged = false;
  private readonly gtMd = toSignal(inject(BreakpointObserver).observe(MEDIA_GT_MD).pipe(map((s) => s.matches)), {initialValue: true});

  readonly columns = computed(() => this.currentSettings().columns);
  readonly dynamicColumns = computed(() => (this.currentSettings().packageDynamicColumns ?? []).filter((c) => c.enabled));
  readonly columnOn = computed(() => _.mapValues(this.columns() ?? {}, (c) => !!c.enabled));

  readonly scrollable = computed(() => {
    const columns = this.columns();
    if (_.isUndefined(columns)) {
      return true;
    }
    const enabled = _.filter(columns, (c) => c.enabled).length;
    return enabled > 5 ? true : !this.gtMd();
  });

  readonly rows = computed<ItemsTableRow[]>(() => {
    const knownPackages = this.splitService.getSitePackageItemsMap() ?? {};
    const rows: ItemsTableRow[] = [];
    const missing = (this.items() ?? []).flatMap((item) => item.missingItems ?? []);
    const attachmentsByReason = _.mapValues(_.keyBy(_.compact(missing), "reasonId"),
      (m) => _.flatMap(m.actions ?? [], (action) => action.attachments ?? []));
    (this.items() ?? []).forEach((item) => {
      const packageId = item.type || item.size;
      rows.push({kind: "item", item, packageId, packageKnown: !!packageId && !!knownPackages[packageId]});
      (item.missingItems ?? []).forEach((m) => rows.push({kind: "missing", item: m, attachments: attachmentsByReason[m.reasonId]}));
    });
    return rows;
  });

  readonly height = computed(() => (this.rows().length ? SERVICE_ITEMS_TABLE_HEADER_HEIGHT + this.rows().length * SERVICE_ITEMS_TABLE_ROW_HEIGHT : null));

  ngOnInit(): void {
    this.currentSettings.set(this.settings());
    Promise.resolve(this.viewSettings.load())
      .then((columnsMap) => {
        if (this.viewSettingsChanged) {
          return;
        }
        const {columns, packageDynamicColumns} = this.currentSettings();
        this.currentSettings.update((settings) => ({...settings, ...this.viewSettings.applyColumnsMap(columnsMap, columns, packageDynamicColumns)}));
      })
      .catch(() => undefined);
  }

  onSettingsChange(settings: PackageDetailsSettings): void {
    this.viewSettingsChanged = true;
    const packageDynamicColumns = settings.packageDynamicColumns
      ?? (this.currentSettings().packageDynamicColumns ?? []).map((column) => ({...column, enabled: false}));
    const next = {...settings, packageDynamicColumns};
    this.currentSettings.set(next);
    this.viewSettings.save(this.viewSettings.toColumnsMap(next.columns, next.packageDynamicColumns));
  }
}
